import React from 'react';
import { Group, Paper, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { UseFormReturnType } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import { SyncRuleFormValues } from '../sync_rule/interface';
import { applyBusyTemplate, applyCopyTemplate, OnboardingTemplateKey } from '../sync_rule/templates';

const TemplateOption: React.FC<{
    selected: boolean;
    title: string;
    description: string;
    onSelect: () => void;
}> = ({ selected, title, description, onSelect }) => (
    <Paper
        component="button"
        type="button"
        role="radio"
        aria-checked={selected}
        onClick={onSelect}
        p="md"
        radius="md"
        withBorder
        bg={selected ? 'primary.0' : undefined}
        styles={{
            root: {
                cursor: 'pointer',
                textAlign: 'left',
                width: '100%',
                borderColor: selected ? 'var(--mantine-color-primary-6)' : undefined,
                borderWidth: selected ? 2 : 1,
                transition: 'border-color 150ms ease, background-color 150ms ease',
                '&:hover': {
                    backgroundColor: selected
                        ? 'var(--mantine-color-primary-0)'
                        : 'var(--mantine-color-gray-0)',
                },
                '&:focus-visible': {
                    outline: '2px solid var(--mantine-color-primary-6)',
                    outlineOffset: 2,
                },
            },
        }}
    >
        <Group justify="space-between" wrap="nowrap" align="flex-start">
            <Stack gap={4}>
                <Text fw={600}>{title}</Text>
                <Text size="sm" c="dimmed">{description}</Text>
            </Stack>
            {selected && (
                <ThemeIcon color="primary" variant="light" size="sm" radius="xl">
                    <IconCheck size={14} stroke={2.5} />
                </ThemeIcon>
            )}
        </Group>
    </Paper>
);

export const OnboardingTemplateChoiceStep: React.FC<{
    form: UseFormReturnType<SyncRuleFormValues>;
    templateChoice: OnboardingTemplateKey | null;
    onTemplateChoice: (choice: OnboardingTemplateKey) => void;
    setDestinationTitle: (s: string) => void;
    setDestinationDescription: (s: string) => void;
    defaultTitle: string;
    defaultDescription: string;
}> = ({
    form,
    templateChoice,
    onTemplateChoice,
    setDestinationTitle,
    setDestinationDescription,
    defaultTitle,
    defaultDescription,
}) => {
    const { t } = useTranslation(['app']);
    const setters = { setDestinationTitle, setDestinationDescription };

    const selectTemplate = (choice: OnboardingTemplateKey) => {
        onTemplateChoice(choice);
        if (choice === 'busy') {
            applyBusyTemplate(form, setters, t('dashboard.sync.template.private.busy-text'));
        } else {
            applyCopyTemplate(form, setters, defaultTitle, defaultDescription);
        }
    };

    return (
        <Stack gap="lg">
            <Stack gap="xs">
                <Text size="xl" fw={600}>{t('dashboard.onboarding.step4.title')}</Text>
                <Text c="dimmed">{t('dashboard.onboarding.step4.description')}</Text>
            </Stack>
            <Stack gap="md" role="radiogroup" aria-label={t('dashboard.onboarding.step4.title')}>
                <TemplateOption
                    selected={templateChoice === 'busy'}
                    title={t('dashboard.onboarding.step4.busy-title')}
                    description={t('dashboard.onboarding.step4.busy-description')}
                    onSelect={() => selectTemplate('busy')}
                />
                <TemplateOption
                    selected={templateChoice === 'copy'}
                    title={t('dashboard.onboarding.step4.copy-title')}
                    description={t('dashboard.onboarding.step4.copy-description')}
                    onSelect={() => selectTemplate('copy')}
                />
            </Stack>
        </Stack>
    );
};
